import { HoldResponseDto, HoldStatus, OrderResponseDto } from '@planning/app/api'
import { EventTypes } from '@planning/messages/eventsTypes'
import { GetHoldsQuery } from '@planning/messages/queries/getHoldsQueryHandler'
import { GetOrderByIdQuery } from '@planning/messages/queries/getOrderByIdQueryHandler'
import { GetOrdersByIdsQuery } from '@planning/messages/queries/getOrdersByIdsQueryHandler'
import { GetOrdersByVisitIdQuery } from '@planning/messages/queries/getOrdersByVisitIdQueryHandler'
import { ItemStore } from '@planning/rt-stores/base/ItemStore'
import { ContainerItemStore } from '@planning/rt-stores/container/ContainerItemStore'
import { IEvent, IMessageBus } from '@tom-ui/utils'
import _ from 'lodash'
import { action, computed, makeObservable } from 'mobx'
import { ContainerJourneyItemStore } from '../../pages/Issues/Stores/ContainerJourneyItemStore'
import { IEntityStore } from '../types'
import { HoldItem, IHoldItem } from './HoldItem'

export class HoldItemStore
  extends ItemStore<HoldResponseDto, IHoldItem>
  implements IEntityStore<IHoldItem>
{
  private hasBeenLoaded = false
  private loadedHoldsJourneyIds = new Set<number>()

  constructor(
    private messageBus: IMessageBus,
    private containerJourneyItemStore: ContainerJourneyItemStore,
    private containerItemStore: ContainerItemStore,
  ) {
    super(
      (key, data) =>
        new HoldItem(this.containerJourneyItemStore, this.containerItemStore, key, data),
    )
    makeObservable(this, {
      receiveHoldsMessage: action,
      receiveOrdersHolds: action,
      receiveOrderHolds: action,
      receiveHoldUpsertedEventMessage: action,
      holdsByContainerJourneyId: computed,
    })

    messageBus.subscribeEvent(GetHoldsQuery.type, this.receiveHoldsMessage)
    messageBus.subscribeEvent(GetOrdersByVisitIdQuery.type, this.receiveOrdersHolds)
    messageBus.subscribeEvent(GetOrdersByIdsQuery.type, this.receiveOrdersHolds)
    messageBus.subscribeEvent(GetOrderByIdQuery.type, this.receiveOrderHolds)
    messageBus.subscribeEvent(EventTypes.OrdersUpsertedEvent, this.receiveOrdersHolds)
    messageBus.subscribeEvent(
      EventTypes.HoldUpsertedInternalEvent,
      this.receiveHoldUpsertedEventMessage,
    )
  }

  fetch = async (includeAll = false) => {
    if (this.hasBeenLoaded) return
    this.hasBeenLoaded = true
    await this.messageBus.dispatchQuery(new GetHoldsQuery(undefined, includeAll))
  }

  async fetchHoldsByContainerJourneyIds(ids: number[]) {
    const idsToLoad = ids.filter(id => !this.loadedHoldsJourneyIds.has(id))
    if (!idsToLoad.length) return

    idsToLoad.forEach(id => this.loadedHoldsJourneyIds.add(id))

    await this.messageBus.dispatchQuery(new GetHoldsQuery(idsToLoad, false))
  }

  receiveHoldsMessage = (event: IEvent<HoldResponseDto[]>): void => {
    if (event.payload) {
      this.upsertBulk(event.payload)

      this.updateLoadedHoldsJourneyIds(event.payload.map(hold => hold.containerJourneyId))
    }
  }

  receiveOrdersHolds = (event: IEvent<OrderResponseDto[]>): void => {
    const holds = event.payload?.flatMap(o => o.holds ?? []) ?? []
    if (holds.length) {
      this.upsertBulk(holds)
      this.updateLoadedHoldsJourneyIds(holds.map(hold => hold.containerJourneyId))
    }
  }

  receiveOrderHolds = (event: IEvent<OrderResponseDto>): void => {
    const holds = event.payload?.holds ?? []
    if (holds.length) this.upsertBulk(holds)
  }

  receiveHoldUpsertedEventMessage = (event: IEvent<HoldResponseDto[]>): void => {
    if (!event.payload) return
    // The event payload is HoldDto-shaped (lacks holdType object). Merge only the fields
    // that are reliably present so we don't clobber holdType data already in the store.
    const merged = event.payload.map(incoming => {
      const existing = this.elements[incoming.id]
      return existing ? { ...existing.data, status: incoming.status, pendingOverrides: incoming.pendingOverrides ?? [] } : incoming
    })
    this.upsertBulk(merged)
    this.updateLoadedHoldsJourneyIds(event.payload.map(hold => hold.containerJourneyId))
  }

  get holdsByContainerJourneyId(): Record<number, HoldResponseDto[]> {
    return _(_.values(this.elements))
      .groupBy(item => item.data.containerJourneyId)
      .mapValues(items => items.map(i => i.data).filter(d => d.status === HoldStatus.Active))
      .value()
  }

  private updateLoadedHoldsJourneyIds(ids: number[]) {
    ids.forEach(id => this.loadedHoldsJourneyIds.add(id))
  }
}
